import { useEffect, useState } from "react";
import { playSfx } from "../audio/sound";
import { now } from "../case/clock";
import { useGameStore } from "../state/gameStore";
import styles from "./shell.module.css";

/**
 * Highlight→corcho (F1-T06, C12): seleccionar texto dentro de cualquier
 * elemento marcado con `data-quotable={artifactId}` ofrece "◇ Fijar cita".
 * La cita viaja como TEXTO PLANO (evento quotePinned) — sin anclas de DOM
 * Range: Roottrees necesitó un parser propio para anclar rangos y el plan
 * presupuesta esa fragilidad; el texto plano es robusto frente a overlays,
 * re-renders e i18n, y para el corcho alcanza (es espacio de pensamiento, B5).
 */

const MAX_QUOTE = 280;

interface Anchor {
  artifactId: string;
  quote: string;
  x: number;
  y: number;
}

export function QuoteLayer() {
  const append = useGameStore((s) => s.append);
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  useEffect(() => {
    const onUp = () => {
      // esperar a que el navegador consolide la selección
      requestAnimationFrame(() => {
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
          setAnchor(null);
          return;
        }
        const text = sel.toString().replace(/\s+/g, " ").trim();
        if (text.length < 3) {
          setAnchor(null);
          return;
        }
        const node = sel.anchorNode;
        const el = node instanceof Element ? node : node?.parentElement;
        const host = el?.closest<HTMLElement>("[data-quotable]");
        if (!host?.dataset.quotable) {
          setAnchor(null);
          return;
        }
        const rect = sel.getRangeAt(0).getBoundingClientRect();
        setAnchor({
          artifactId: host.dataset.quotable,
          quote: text.length > MAX_QUOTE ? `${text.slice(0, MAX_QUOTE - 1)}…` : text,
          x: rect.left + rect.width / 2,
          y: rect.top,
        });
      });
    };
    const onDown = (e: PointerEvent) => {
      if (!(e.target instanceof Element) || !e.target.closest(`.${styles.quoteBtn}`)) {
        setAnchor(null);
      }
    };
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointerdown", onDown);
    };
  }, []);

  if (!anchor) return null;

  const pin = () => {
    append({ t: "quotePinned", artifactId: anchor.artifactId, quote: anchor.quote, at: now() });
    playSfx("pin");
    window.getSelection()?.removeAllRanges();
    setAnchor(null);
  };

  return (
    <button
      type="button"
      className={styles.quoteBtn}
      style={{ left: anchor.x, top: anchor.y }}
      data-testid="quote-pin"
      onClick={pin}
    >
      ◇ Fijar cita
    </button>
  );
}
